import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Header from '@/components/Header';
import BottomNavigation from '@/components/BottomNavigation';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { User } from '@shared/schema';

const Profile: React.FC = () => {
  const [userId, setUserId] = useState<number | null>(null);

  // Fetch or create default user
  const { data: userData } = useQuery<User>({
    queryKey: ['/api/default-user'],
    onSuccess: (data) => {
      setUserId(data.id);
    }
  });

  // Settings that would be saved to user preferences in a real app
  const [notifications, setNotifications] = useState(true);
  const [dailyReminders, setDailyReminders] = useState(true);
  const [weeklyReports, setWeeklyReports] = useState(true);
  const [workoutDuration, setWorkoutDuration] = useState(30);

  return (
    <div className="max-w-md mx-auto min-h-screen bg-white shadow-lg relative">
      <Header title="Profile" subtitle="Manage your preferences" />
      
      <div className="p-4 overflow-y-auto pb-20" style={{ maxHeight: 'calc(100vh - 72px)' }}>
        {userData ? (
          <>
            <div className="flex items-center mb-6">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-white text-xl font-bold mr-4">
                {userData.name.charAt(0)}
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold">{userData.name}</h2>
                <p className="text-sm text-gray-500">@{userData.username}</p>
              </div>
            </div>
            
            <Card className="mb-4">
              <CardHeader>
                <CardTitle>Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-medium">Notifications</h3>
                    <p className="text-sm text-gray-500">Receive workout reminders</p>
                  </div>
                  <Switch 
                    checked={notifications}
                    onCheckedChange={setNotifications}
                  />
                </div>
                
                <Separator />
                
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-medium">Daily Reminders</h3>
                    <p className="text-sm text-gray-500">Get daily workout notifications</p>
                  </div>
                  <Switch 
                    checked={dailyReminders}
                    onCheckedChange={setDailyReminders}
                    disabled={!notifications}
                  />
                </div>
                
                <Separator />
                
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-medium">Weekly Reports</h3>
                    <p className="text-sm text-gray-500">Receive weekly progress summary</p>
                  </div>
                  <Switch 
                    checked={weeklyReports}
                    onCheckedChange={setWeeklyReports}
                    disabled={!notifications}
                  />
                </div>
              </CardContent>
            </Card>
            
            <Card className="mb-4">
              <CardHeader>
                <CardTitle>Workout Preferences</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-medium mb-2">Workout Duration</h3>
                  <p className="text-sm text-gray-500 mb-4">Target workout time: {workoutDuration} minutes</p>
                  <Slider
                    value={[workoutDuration]}
                    onValueChange={(value) => setWorkoutDuration(value[0])}
                    min={10}
                    max={60}
                    step={5}
                  />
                </div>
                
                <Separator />
                
                <div>
                  <h3 className="font-medium mb-2">Joker Days</h3>
                  <p className="text-sm text-gray-500 mb-2">Max joker days per week: {userData.maxJokerDays}</p>
                  <p className="text-xs text-gray-400">You can use joker days to skip workouts without affecting your streak.</p>
                </div>
                
                <Separator />
                
                <div>
                  <h3 className="font-medium mb-2">Rest Days</h3>
                  <p className="text-sm text-gray-500 mb-2">Max rest days per week: 1</p>
                  <p className="text-xs text-gray-400">Rest days are important for recovery and are part of your workout plan.</p>
                </div>
              </CardContent>
            </Card>
            
            <Button className="w-full mb-4" variant="outline">
              Save Preferences
            </Button>
            
            <div className="text-center mb-6">
              <p className="text-xs text-gray-400">
                App Version 1.0.0
              </p>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-64">
            <p>Loading profile...</p>
          </div>
        )}
      </div>
      
      <BottomNavigation />
    </div>
  );
};

export default Profile;
